import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Briefcase, Users, Calendar, MessageCircle, Settings, LogOut, ClipboardList } from "lucide-react";
import AdminDropdown from "@/components/AdminDropdown";
import NotificationBell from "@/components/NotificationBell";

export default function Layout({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/auth");
  };

  const navItems = [
    { path: "/jobs", label: "Jobs", icon: Briefcase, testId: "nav-jobs" },
    { path: "/community", label: "Community", icon: Users, testId: "nav-community" },
    { path: "/events", label: "Events", icon: Calendar, testId: "nav-events" },
    { path: "/messages", label: "Messages", icon: MessageCircle, testId: "nav-messages" }
  ];

  if (user?.role === "student" || user?.role === "graduate") {
    navItems.push({ path: "/applications", label: "My Applications", icon: ClipboardList, testId: "nav-applications" });
  }

  if (user?.role === "employer") {
    navItems.push({ path: "/employer", label: "My Jobs", icon: Briefcase, testId: "nav-employer" });
  }

  if (user?.role === "admin") {
    navItems.push({ path: "/admin", label: "Admin", icon: Settings, testId: "nav-admin" });
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#FAFAFA' }}>
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/jobs")}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-teal-500 flex items-center justify-center text-white text-lg font-bold">
                NT
              </div>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk', color: '#FF6B6B' }}>
                  NextGen Trades Academy
                </h1>
                <p className="text-xs text-gray-600">Community Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user?.role === "admin" && <AdminDropdown user={user} />}
              <NotificationBell />
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-gray-600 hover:text-red-600"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white shadow-sm border-b sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={item.testId}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-teal-500 text-teal-600 font-medium"
                      : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      <footer className="bg-white border-t py-6 mt-12 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              <p>&copy; {new Date().getFullYear()} NextGen Trades Academy. All rights reserved.</p>
            </div>
            <div className="flex gap-6 text-sm">
              <a href="/terms" className="text-gray-600 hover:text-teal-600 transition-colors">Terms of Service</a>
              <a href="/privacy" className="text-gray-600 hover:text-teal-600 transition-colors">Privacy Policy</a>
              <a href="/guidelines" className="text-gray-600 hover:text-teal-600 transition-colors">Community Guidelines</a>
              <a href="mailto:compliance@ngta.com" className="text-gray-600 hover:text-teal-600 transition-colors">Report Issue</a>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500 text-center">
            <p>NextGen Trades Academy is not an employment agency. Job postings are provided by third-party employers.</p>
            <p className="mt-1">We are committed to maintaining a harassment-free environment in compliance with California law.</p>
          </div>
        </div>
      </footer>

      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg mobile-nav-container">
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {navItems.slice(0, 4).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`mobile-${item.testId}`}
                className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg transition-colors ${
                  isActive
                    ? "bg-teal-50 text-teal-600"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="md:hidden h-20"></div>
    </div>
  );
}